import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useThemeMode, ThemeColorName } from './contexts/ThemeContext';
import { CenterProvider } from './contexts/CenterContext';
import ErrorBoundary from './components/ErrorBoundary';
import { IdleTimeoutGuard } from './components/IdleTimeoutGuard';
const StudyManagement = lazy(() => import('./pages/StudyManagement'));
const ViewerModule = lazy(() => import('./pages/ViewerModule'));
const ReadingModule = lazy(() => import('./pages/ReadingModule'));
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CustomFields = lazy(() => import('./pages/CustomFields'));
const NewRegistrations = lazy(() => import('./pages/NewRegistrations'));
const StudyDetail = lazy(() => import('./pages/StudyDetail'));
const StudyEdit = lazy(() => import('./pages/StudyEdit'));
const CenterSettings = lazy(() => import('./pages/CenterSettings'));
const TemplatesManagement = lazy(() => import('./pages/TemplatesManagement'));

const UserManagement = lazy(() => import('./pages/UserManagement'));
const ManagePages = lazy(() => import('./pages/ManagePages'));
const ManageViewers = lazy(() => import('./pages/ManageViewers'));
const ViewerPreferences = lazy(() => import('./pages/ViewerPreferences'));
const SortPreference = lazy(() => import('./pages/SortPreference'));
const PDFTemplates = lazy(() => import('./pages/PDFTemplates'));
const AISettings = lazy(() => import('./pages/AISettings'));
const ImagePrintSettings = lazy(() => import('./pages/ImagePrintSettings'));
const StudyExcelExport = lazy(() => import('./pages/StudyExcelExport'));
const MobileView = lazy(() => import('./pages/MobileView'));
const MobileReadingModule = lazy(() => import('./pages/MobileReadingModule'));
// Loading component
const LoadingScreen: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
    }}
  >
    <Box>Loading...</Box>
  </Box>
);

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

// Role Protected Route Component
const RoleProtectedRoute: React.FC<{ children: React.ReactNode; allowedRoles: string[]; pageKey?: string }> = ({
  children,
  allowedRoles,
  pageKey
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }

  if (user && pageKey && user.disabled_pages?.includes(pageKey)) {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
};

// App Routes Component
const AppRoutes: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { setThemeColor } = useThemeMode();
  const location = useLocation();

  useEffect(() => {
    const savedColor = user?.theme_color as ThemeColorName | undefined;
    if (savedColor) {
      setThemeColor(savedColor);
    }
  }, [user, setThemeColor]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
    <Suspense fallback={<LoadingScreen />}>
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login />}
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/custom-fields"
        element={
          <RoleProtectedRoute allowedRoles={['admin']}>
            <CustomFields />
          </RoleProtectedRoute>
        }
      />


      <Route
        path="/templates"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'radiologist']}>
            <TemplatesManagement />
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/pdf-templates"
        element={
          <RoleProtectedRoute allowedRoles={['admin']}>
            <PDFTemplates />
          </RoleProtectedRoute>
        }
      />


      <Route
        path="/ai-settings"
        element={
          <RoleProtectedRoute allowedRoles={["admin"]}>
            <AISettings />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/image-print-settings"
        element={
          <RoleProtectedRoute allowedRoles={["admin"]}>
            <ImagePrintSettings />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/study-excel-export"
        element={
          <RoleProtectedRoute allowedRoles={["admin"]}>
            <StudyExcelExport />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <RoleProtectedRoute allowedRoles={['admin']}>
            <UserManagement />
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/new-registrations"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'center_staff', 'technician']} pageKey="new_registrations">
            <NewRegistrations />
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/study-management"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'radiologist', 'center_staff', 'technician']} pageKey="study_management">
            <StudyManagement />
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/viewer"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'radiologist', 'viewer', 'technician']} pageKey="viewer_module">
            <ViewerModule />
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/mobile-view"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'radiologist', 'center_staff', 'viewer', 'technician']} pageKey="mobile_view">
            <MobileView />
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <RoleProtectedRoute allowedRoles={['admin']}>
            <CenterSettings />
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/manage-pages"
        element={
          <RoleProtectedRoute allowedRoles={['admin']}>
            <ManagePages />
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/manage-viewers"
        element={
          <RoleProtectedRoute allowedRoles={['admin']}>
            <ManageViewers />
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/viewer-preferences"
        element={
          <RoleProtectedRoute allowedRoles={['radiologist']}>
            <ViewerPreferences />
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/sort-preference"
        element={
          <ProtectedRoute>
            <SortPreference />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reporting"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'radiologist']} pageKey="reading_module">
            <ReadingModule />
          </RoleProtectedRoute>
        }
      />
      <Route path="/reading-module" element={<Navigate to={{ pathname: '/reporting', search: location.search }} replace />} />

      <Route
        path="/mobile-reporting"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'radiologist']} pageKey="reading_module">
            <MobileReadingModule />
          </RoleProtectedRoute>
        }
      />
      <Route path="/mobile-reading-module" element={<Navigate to={{ pathname: '/mobile-reporting', search: location.search }} replace />} />

      <Route
        path="/studies/:id/edit"
        element={
          <RoleProtectedRoute allowedRoles={['admin', 'radiologist', 'center_staff', 'technician']}>
            <StudyEdit />
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/studies/:id"
        element={
          <ProtectedRoute>
            <StudyDetail />
          </ProtectedRoute>
        }
      />
      {/* Redirect root and unknown routes */}
      <Route path="/" element={<Navigate to="/dashboard" />} />
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
    </Suspense>
    </>
  );
};

// Wraps the routed page in an ErrorBoundary keyed by path, so an uncaught
// render error on one page doesn't strand every other route behind it.
const RoutesWithErrorBoundary: React.FC = () => {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname}>
      <AppRoutes />
    </ErrorBoundary>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <CenterProvider>
        <AuthProvider>
          <Router>
            <IdleTimeoutGuard />
            <RoutesWithErrorBoundary />
          </Router>
        </AuthProvider>
      </CenterProvider>
    </ThemeProvider>
  );
};

export default App;
